import type { ProportionalToCoverage } from '@/features/quotes/types/group-life/coverage-schema.types';
import type { CoverageSelection } from '@/features/quotes/utils/group-life/coverage-resolver';
import { getDisplaySumInsuredMax } from '@/features/quotes/utils/group-life/coverage-display.util';
import {
    clampPercentToMax,
    formatCurrencyBRL,
    formatPercentValue,
    parsePercentInput,
} from '@/features/quotes/utils/group-life/format';
import { CoverageRowShell, PercentControl } from './coverage-row-shell';

type Props = {
  coverage: ProportionalToCoverage;
  selection: CoverageSelection | undefined;
  resolvedCapital: number | null;
  referenceCoverageName?: string;
  onChange: (next: CoverageSelection) => void;
  disabled?: boolean;
  error?: string;
  /** Coberturas básicas não exibem o detalhamento "X% de Y" nem o valor resolvido. */
  isBasic?: boolean;
};

export function ProportionalToCoverageRow({
  coverage,
  selection,
  resolvedCapital,
  referenceCoverageName,
  onChange,
  disabled,
  error,
  isBasic,
}: Props) {
  const enabled =
    selection?.kind === 'PROPORTIONAL_TO_COVERAGE' ? selection.enabled : false;
  const override =
    selection?.kind === 'PROPORTIONAL_TO_COVERAGE'
      ? selection.proportionOverride
      : undefined;
  const proportion = Math.max(0, override ?? coverage.proportion);
  const refName = referenceCoverageName ?? coverage.referenceCoverageCode;
  // Teto de capital (`sumInsuredMax`): mesmo rótulo das coberturas DECLARED.
  // Aqui não bloqueia — o capital é derivado do percentual e o resolver o
  // LIMITA ao teto (o valor exibido já vem limitado). Exibido também nas
  // básicas (que não mostram o detalhamento "X% de Y").
  const displayMax = getDisplaySumInsuredMax(coverage);
  const maxLabel =
    displayMax != null ? `Max: ${formatCurrencyBRL(displayMax)}` : undefined;

  return (
    <CoverageRowShell
      name={coverage.name}
      description={coverage.description}
      badge={coverage.isRider ? 'Adicional' : undefined}
      caption={
        isBasic ? (
          maxLabel
        ) : (
          <>
            {formatPercentValue(proportion)}% de <span className='font-medium'>{refName}</span>
            {maxLabel ? ` · ${maxLabel}` : ''}
          </>
        )
      }
      enabled={enabled}
      onToggle={next =>
        onChange({
          kind: 'PROPORTIONAL_TO_COVERAGE',
          enabled: next,
          proportionOverride: override,
        })
      }
      disabled={disabled}
      error={error}
      control={
        <PercentControl
          percent={formatPercentValue(proportion)}
          onChangePercent={raw =>
            onChange({
              kind: 'PROPORTIONAL_TO_COVERAGE',
              enabled,
              // Limita o percentual ao teto da cobertura (`Proportion` do schema).
              proportionOverride: clampPercentToMax(
                parsePercentInput(raw),
                coverage.proportion,
              ),
            })
          }
          resolvedLabel={
            isBasic
              ? undefined
              : enabled
                ? formatCurrencyBRL(resolvedCapital)
                : // Reserva a linha (vazia) para a altura não mudar ao ligar.
                  ''
          }
          disabled={disabled || !enabled}
        />
      }
    />
  );
}
